const versionServiceDetectionMixin = {
  /**
   * Get main JS bundle filename from index.html for version comparison
   * This serves as a reliable fallback since JS filenames contain content hashes
   */
  async getMainJsHash() {
    const cacheBust = Date.now();
    const isDevelopment = process.env.NODE_ENV === 'development';

    try {
      // Fetch the index.html to extract the main JS filename
      const indexUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/index.html?v=${cacheBust}`;
      const response = await fetch(indexUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        const html = await response.text();
        // Extract main.*.js filename - matches patterns like main.abc123.js
        const jsMatch = html.match(/main\.([a-f0-9]+)\.js/i);
        if (jsMatch && jsMatch[1]) {
          return jsMatch[1]; // Return just the hash portion
        }
      }
    } catch (error) {
      if (!isDevelopment) {
        console.warn('Could not fetch index.html for JS hash:', error);
      }
    }

    return null;
  },

  /**
   * Get current version from API endpoint with aggressive cache-busting
   */
  async getCurrentVersion() {
    const cacheBust = Date.now();
    const isDevelopment = process.env.NODE_ENV === 'development';

    try {
      // Try to get version from API endpoint first (most reliable)
      const apiUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/api/version?v=${cacheBust}&t=${cacheBust}&cb=${Math.random()}`;
      const response = await fetch(apiUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const versionData = await response.json();
          // Store the full version data for later use
          this.versionData = versionData;

          // Use buildHash as primary version identifier
          if (versionData.buildHash) {
            return versionData.buildHash;
          }
          if (versionData.version) {
            return versionData.version;
          }
          if (versionData.buildTime) {
            return versionData.buildTime;
          }
        } else if (!isDevelopment) {
          // Only warn in production if we get non-JSON response
          console.warn('Version API returned non-JSON response');
        }
      }
    } catch (error) {
      // Only log warnings in production, silently fail in development
      if (!isDevelopment) {
        console.warn('Could not fetch version from API:', error);
      }
    }

    // Fallback: Try to get JS hash from index.html
    const jsHash = await this.getMainJsHash();
    if (jsHash) {
      return jsHash;
    }

    try {
      // Fallback: try to get version from manifest.json with multiple cache-busting parameters
      const manifestUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/manifest.json?v=${cacheBust}&t=${cacheBust}&cb=${Math.random()}`;
      const response = await fetch(manifestUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const manifest = await response.json();
          if (manifest.version) {
            return manifest.version;
          }
        } else if (!isDevelopment) {
          // Only warn in production if we get non-JSON response
          console.warn('manifest.json returned non-JSON response');
        }
      }
    } catch (error) {
      // Only log warnings in production, silently fail in development
      if (!isDevelopment) {
        console.warn('Could not fetch manifest.json:', error);
      }
    }

    try {
      // Fallback: try to get version from meta.json with aggressive cache-busting
      const metaUrl = `${process.env.NODE_ENV === 'production' ? '/portal' : ''}/meta.json?v=${cacheBust}&t=${cacheBust}&cb=${Math.random()}`;
      const response = await fetch(metaUrl, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        // Check if response is actually JSON before parsing
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const meta = await response.json();
          if (meta.version) {
            return meta.version;
          }
          if (meta.buildHash) {
            return meta.buildHash;
          }
          if (meta.buildTime) {
            return meta.buildTime;
          }
        } else if (!isDevelopment) {
          // Only warn in production if we get non-JSON response
          console.warn('meta.json returned non-JSON response');
        }
      }
    } catch (error) {
      // Only log warnings in production, silently fail in development
      if (!isDevelopment) {
        console.warn('Could not fetch meta.json:', error);
      }
    }

    // Final fallback: use a stable version identifier
    return process.env.REACT_APP_VERSION || '1.0.0';
  },
};

export default versionServiceDetectionMixin;
